import {
  Drawer,
  DrawerProps,
  Checkbox,
  Switch,
  Spin,
  Button,
  Radio,
  message,
  Menu,
  Dropdown,
  Tooltip,
  Space,
  Popconfirm,
  Badge,
  Modal,
  Tabs,
} from 'antd';
import { HTMLAttributes, memo, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { css, cx } from '@emotion/css';
import styled from '@emotion/styled';
import SafeImage from 'components/SafeImage';
import { useStoreInfo } from '@knowledge/common/components/ItemSelectV2/useStoreInfo';
import {
  DownOutlined,
  EditOutlined,
  LinkOutlined,
  UpOutlined,
  SwapOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  countSku,
  SpuSelectMap,
  ToggleSku,
  ToggleSpu,
  convertAtoB,
  useSelectControl,
  splitItems,
  mergeItems,
  getSpuInfoBySelectMap,
} from '@knowledge/common/helpers/sku';
import Empty from 'components/Empty';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import ProgressBar from './ProgressBar';
import { ReactComponent as DangerSvg } from '@knowledge/assets/danger.svg';
import Icon, { LeftOutlined } from '@ant-design/icons';
import {
  getMultiLangValue,
  segments2text,
  transformItemV2Spu2KnowledgeSpu,
} from '@knowledge/knowledges/singleClass/helpers';
import type { Knowledge, Segment } from './type';
import { useGetSegmentsValue } from '@knowledge/common/share';
import {
  EditDangerousKCDrawer,
  EditDangerousKCDrawerSelfProps,
  EditKCDrawer,
  EditKCDrawerSelfProps,
} from './EditKCDrawer';
import useModal from 'hooks/useModal';
import { useSpuKnowledgeMutate } from '@knowledge/knowledges/spu/crud';
import {
  differenceBy,
  findIndex,
  keys,
  pick,
  sortBy,
  uniq,
  map,
  find,
  gt,
  get,
  filter,
  reduce,
  forEach,
  orderBy,
  cloneDeep,
} from 'lodash-es';
import { useLatest, useMemoizedFn, useRequest, useScroll, useSize, useUpdateEffect } from 'ahooks';
import {
  getSpuClassKnowledgeDetail,
  updateKnowledgeStatus,
} from '@knowledge/knowledges/singleClassNew/api';
import { useFormikContext } from 'formik';
import type { SpuAreaFormikValue } from './type';
import { ConfigedSkuButton } from './ConfigedSkuModal';
import { CollapseOperationInSingleClassNew, collapseOperationState } from './CollapseOperation';
import ClosedKnowledge from './ClosedKnowledge';
import { useSpuClassContext } from './SpuClass';
import isEqual from 'react-fast-compare';
import { ConfigDefaultIcon } from '@knowledge/knowledges/singleClass/ConfigDefault';
import { useAtomValue } from 'jotai';

type KnowledgeValue = Knowledge['values'][number];

export interface ConfigKCDynamicProps {
  items: ItemSelectV2.Item[];
}
interface ConfigKCProps extends DrawerProps, ConfigKCDynamicProps {
  loading: boolean;
  knowledges: Knowledge[];
  spuClassSpus: ItemSelectV2.Item[]; // 单品下的商品
  spuClassIds?: number[];
}

/**
 * 该页面下的编辑实际走的是新增逻辑，需要复制一份知识值保存
 * 另外，第一次编辑走新增，之后再对同一个key做编辑则是走更新，这个逻辑需要在组件内部处理
 * 有很多tricky的逻辑，以后谁维护谁倒霉
 */
const ConfigKC = memo((props: ConfigKCProps) => {
  const {
    className,
    items,
    spuClassSpus,
    knowledges: _knowledges,
    loading,
    spuClassIds,
    ...restProps
  } = props;
  const [midKnowledges, setMidKnowledges] = useState(_knowledges);
  useUpdateEffect(() => {
    setMidKnowledges(_knowledges);
  }, [_knowledges]);
  const { activeSpuClass, activeIndex, setActiveIndex, list } = useSpuClassContext();
  const knowledges = useMemo<Knowledge[]>(() => {
    const itemsMap = convertAtoB(items);
    return map(midKnowledges, (each) => {
      return {
        ...each,
        values: map(each.values, (value) => {
          return {
            ...value,
            spus: filter(
              map(value.spus, (spu) => {
                const exist = itemsMap[spu.itemInternalId];
                if (exist) {
                  const afterSkus = filter(
                    map(spu.skus, (sku) => {
                      if (get(exist, `skus.${sku.skuId}`)) {
                        return sku;
                      } else {
                        return undefined;
                      }
                    }),
                    Boolean,
                  );
                  if (spu.skus.length > 0 && afterSkus.length === 0) {
                    return undefined!;
                  }
                  return {
                    ...spu,
                    skus: afterSkus,
                  };
                } else {
                  return undefined!;
                }
              }),
              Boolean,
            ),
            originSpus: value.originSpus || value.spus,
          };
        }),
      } as Knowledge;
    });
  }, [midKnowledges, items]);
  const knowledgesForCompleteness = useMemo<Knowledge[]>(() => {
    const itemsMap = convertAtoB(items);
    return map(_knowledges, (each) => {
      return {
        ...each,
        values: map(each.values, (value) => {
          return {
            ...value,
            spus: filter(
              map(value.spus, (spu) => {
                const exist = itemsMap[spu.itemInternalId];
                if (exist) {
                  const afterSkus = filter(
                    map(spu.skus, (sku) => {
                      if (get(exist, `skus.${sku.skuId}`)) {
                        return sku;
                      } else {
                        return undefined;
                      }
                    }),
                    Boolean,
                  );
                  if (spu.skus.length > 0 && afterSkus.length === 0) {
                    return undefined!;
                  }
                  return {
                    ...spu,
                    skus: afterSkus,
                  };
                } else {
                  return undefined!;
                }
              }),
              Boolean,
            ),
            originSpus: value.spus,
          };
        }),
      } as Knowledge;
    });
  }, [_knowledges, items]);
  const { t } = useTranslate();
  const [selectedKnowledgeId, setSelectedKnowledgeId] = useState<string>(undefined!); // 知识点的id 261, 219
  const selectedKnowledge = find(knowledges, { id: selectedKnowledgeId });
  const lang = useSystemLanguageValue();
  const [newAddedIds, setNewAddedIds] = useState<string[]>([]); // 新增的id维护在这里，排序在前
  const [fromUpdateIds, setFromUpdateIds] = useState<string[]>([]); // 编辑的时候需要分裂一个新配置出来，这个配置的id维护在这里，下次编辑则不需要分裂了
  // const [replaceIdMap, setReplaceIdMap] = useState<Record<string, string>>({}); // 从编辑状态切换到新增状态的时候，需要隐藏的原来的id，展示新的id
  const configedSpusMap = useMemo<Record<string, any>>(() => {
    if (!selectedKnowledge) {
      return {} as Record<string, any>;
    } else {
      return reduce(
        selectedKnowledge.values,
        (acc, cur) => {
          forEach(cur.spus, (spu) => {
            !acc[spu.itemInternalId] && (acc[spu.itemInternalId] = true);
            forEach(spu.skus, (sku) => {
              acc[spu.itemInternalId] = {
                ...acc[spu.itemInternalId],
                [sku.skuId]: true,
              };
            });
          });
          return acc;
        },
        {} as Record<string, any>,
      );
    }
  }, [selectedKnowledge]);
  const filterSpus = useMemo(() => {
    return filter(
      map(items, (each) => {
        if (each.skuInfo.length > 0) {
          const skus = filter(each.skuInfo, (sku) => {
            return !get(configedSpusMap, `${each.internalItemId}.${sku.skuId}`);
          });
          if (skus.length === 0) {
            return undefined!;
          } else {
            return {
              ...each,
              skuInfo: skus,
            };
          }
        } else {
          if (!get(configedSpusMap, each.internalItemId)) {
            return each;
          }
          return undefined!;
        }
      }),
      Boolean,
    );
  }, [items, configedSpusMap]);
  const [firstOrderMap, setFirstOrderMap] = useState<Map<string, number>>(new Map());
  const selectedKnowledgeRef = useLatest(selectedKnowledge);
  useEffect(() => {
    const sorted = sortBy(selectedKnowledgeRef.current?.values, (each) => {
      const count = countSku(each.spus || [], 'skus');
      return -count;
    });
    setFirstOrderMap(new Map(sorted.map((each, index) => [each.id, index])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKnowledgeId]);
  const renderKnowledgeValues = useMemo(() => {
    const orderMap = new Map(newAddedIds.map((id, index) => [id, index]));
    const maxIndex = newAddedIds.length;
    const sorted1 = sortBy(selectedKnowledge?.values, (each) => {
      return firstOrderMap.has(each.id) ? firstOrderMap.get(each.id) : 0;
    });
    const sorted = sortBy(sorted1, (each) => {
      return orderMap.has(each.id) ? orderMap.get(each.id) : maxIndex;
    });
    // Object.entries(replaceIdMap).forEach(([from, to]) => {
    //   const toIndex = findIndex(sorted, { id: to });
    //   const spliced = sorted.splice(toIndex, 1);
    //   const fromIndex = findIndex(sorted, { id: from });
    //   sorted.splice(fromIndex, 1, spliced[0]);
    // });
    return sortBy(sorted, (e) => (e?.isDefaultValue ? 0 : 1));
    // }, [selectedKnowledge, newAddedIds, replaceIdMap, firstOrderMap]);
  }, [selectedKnowledge, newAddedIds, firstOrderMap]);
  const filterSpusIds = useMemo(() => {
    return map(filterSpus, (each) => each.internalItemId);
  }, [filterSpus]);
  const [spuSelectMap, selectedSkuLen, { toggleSku, toggleSpu, toggleBatch, getBatchStatus }] =
    useSelectControl(filterSpus, undefined, {
      initialSelectAll: true,
      keepPrevSelected: true,
    });
  const { checked, indeterminate } = useMemo(() => {
    return getBatchStatus(filterSpusIds);
  }, [filterSpusIds, getBatchStatus]);
  const [knowledgeValueId, setKnowledgeValueId] = useState<string>(undefined!); // 知识值的id
  const { getSegmentsValue } = useGetSegmentsValue(selectedKnowledge?.valueAsDirectAnswer);
  const {
    visible: visible1,
    show: show1,
    hide: hide1,
    meta: meta1,
  } = useModal<EditKCDrawerSelfProps>();
  const {
    visible: visible2,
    show: show2,
    hide: hide2,
    meta: meta2,
  } = useModal<EditDangerousKCDrawerSelfProps>();
  const { update } = useSpuKnowledgeMutate();
  const spuRegions = useMemo(() => {
    return uniq(
      map(items, (item) => {
        return item.region;
      }),
    );
  }, [items]);
  const { setFieldValue } = useFormikContext<SpuAreaFormikValue>();
  const { runAsync: refreshKnowledgeDetail } = useRequest(
    () =>
      getSpuClassKnowledgeDetail({
        spuClassId: selectedKnowledge!.spuClassId,
        knowledgeId: selectedKnowledgeId,
      }),
    {
      ready: !!selectedKnowledgeId,
      manual: true,
      onError: () => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      },
      onSuccess: (res) => {
        const newArr = [..._knowledges];
        const index = findIndex(_knowledges, { id: selectedKnowledgeId });
        newArr.splice(index, 1, res);
        setFieldValue('knowledges', newArr);
      },
    },
  );
  const shutDownInfo = useMemo(() => {
    return reduce(
      _knowledges,
      (acc, cur) => {
        if (cur.isShutdown) {
          acc.push(cur.id);
        }
        return acc;
      },
      [] as string[],
    );
  }, [_knowledges]);
  const handleKc = (value?: KnowledgeValue) => {
    if (selectedKnowledge) {
      const segments = getSegmentsValue(value || ({} as any));
      const callback = (
        part: Partial<KnowledgeValue>,
        isDefaultValue: boolean,
      ): Promise<unknown> => {
        let newKnowledge: Knowledge = undefined!;
        if (value) {
          // 需要从value复制
          if (fromUpdateIds.indexOf(value.id) > -1) {
            newKnowledge = {
              ...selectedKnowledge,
              values: [
                ...map(selectedKnowledge.values, (each) => {
                  const { spus, originSpus, ...others } = each;
                  if (each.id === value.id) {
                    return {
                      ...others,
                      spus: originSpus,
                      ...part,
                      isDefaultValue,
                    };
                  } else {
                    return {
                      ...others,
                      spus: originSpus,
                    };
                  }
                }),
              ],
            } as any;
          } else {
            newKnowledge = {
              ...selectedKnowledge,
              values: [
                ...map(selectedKnowledge.values, (each) => {
                  if (each.id === value.id) {
                    return {
                      ...each,
                      spus: splitItems(each.originSpus!, each.spus) as any,
                    };
                  }
                  return {
                    ...each,
                    spus: each.originSpus,
                  };
                }),
                {
                  ...part,
                  spus: find(selectedKnowledge.values, { id: value.id })?.spus || [],
                  isDefaultValue,
                },
              ],
            } as any;
          }
        } else {
          // 生成一份新的
          newKnowledge = {
            ...selectedKnowledge,
            values: [
              ...map(selectedKnowledge.values, (each) => {
                const { spus, originSpus, ...others } = each;
                return {
                  ...others,
                  spus: originSpus,
                };
              }),
              {
                ...part,
                spus: [],
                isDefaultValue,
              },
            ],
          } as any;
        }

        return update({
          spuKnowledge: newKnowledge as any,
        }).then(() => {
          return refreshKnowledgeDetail().then((res) => {
            message.success(t('已保存'));
            const diffId = map(differenceBy(res.values, selectedKnowledge.values, 'id'), 'id'); // 新增的
            if (value) {
              // 说明是复制的
              if (diffId.length > 0) {
                // setReplaceIdMap((prev) => {
                //   return {
                //     ...prev,
                //     [value.id]: diffId?.[0],
                //   };
                // });
                knowledgeValueId && setKnowledgeValueId(diffId?.[0]);
              }
              setFromUpdateIds((prev) => [...prev, ...diffId]);
            } else {
              // 说明是新增的
              setNewAddedIds((prev) => {
                return [...diffId, ...prev];
              });
              setKnowledgeValueId(diffId?.[0]); // 新增的聚焦id
            }
          });
        });
      };

      if (selectedKnowledge.valueAsDirectAnswer) {
        const regionKCItemDefaultValues: Record<string, Segment[]> | undefined = (() => {
          if (!value) return undefined;
          try {
            const map = JSON.parse(value.segmentsMultiLang);
            const rmap = pick(map, spuRegions);
            if (keys(rmap).length === 0) {
              rmap[spuRegions[0]] = getSegmentsValue(value);
            }
            return rmap;
          } catch (e) {
            console.log(e);
            return undefined;
          }
        })();

        show2({
          regions: spuRegions,
          defaultValues: regionKCItemDefaultValues,
          onConfirm: (values, checked) => {
            const part = {
              segments: Object.values(values)?.[0],
              segmentsMultiLang: JSON.stringify(values),
            };
            return callback(part, checked);
          },
        });
      } else {
        show1({
          value: gt(get(segments, 'length', 0), 0) ? segments : undefined,
          onChange: (values, checked) => {
            return callback(
              {
                segments: values,
              },
              checked,
            );
          },
        });
      }
    }
  };
  const knowledgeCompleteness = useMemo<Record<string, number>>(() => {
    const skuCount = countSku(items, 'skuInfo');
    return knowledgesForCompleteness.reduce((acc, curk) => {
      const son = reduce(
        curk.values,
        (acc, cur) => {
          forEach(cur.spus, (spu) => {
            if (spu.skus.length) {
              acc += spu.skus.length;
            } else {
              acc += 1;
            }
          });
          return acc;
        },
        0,
      );
      const parent = skuCount;
      acc[curk.id] = parent === 0 ? 0 : Math.floor((son / parent) * 100);
      return acc;
    }, {} as Record<string, number>);
  }, [knowledgesForCompleteness, items]);
  const [knowledgeSort, setKnowledgeSort] = useState<'1' | '2'>('1');
  const [knowledgeSortVisible, setKnowledgeSortVisible] = useState(false);
  const knowledgesRef = useLatest(knowledges);
  const knowledgeSortRef = useLatest(knowledgeSort);
  const knowledgeCompletenessRef = useLatest(knowledgeCompleteness);
  const sortKnowledgeAction = useMemoizedFn(() => {
    if (knowledgeSortRef.current === '1') {
      return orderBy(
        knowledgesRef.current,
        ['valueAsDirectAnswer', (each) => knowledgeCompletenessRef.current[each.id]],
        ['desc', 'desc'],
      );
    } else {
      return orderBy(
        knowledgesRef.current,
        [(each) => knowledgeCompletenessRef.current[each.id]],
        ['asc'],
      );
    }
  });
  const [sortedKnowledges, setSortedKnowledges] = useState(() => {
    return sortKnowledgeAction();
  }); // 只有在1、排序规则改变时才需要重新排序
  useEffect(() => {
    setTimeout(() => {
      setSortedKnowledges(sortKnowledgeAction());
    });
    // 不能监听knowledges, 只能监听loading变化，并且需要异步更新
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledgeSort, loading]);
  const selectedKnowledgeIndex = useMemo(() => {
    return findIndex(sortedKnowledges, { id: selectedKnowledgeId });
  }, [sortedKnowledges, selectedKnowledgeId]);
  const [saveLoading, setSaveLoading] = useState(false);
  const handleSave = (next?: boolean) => {
    // if (filterSpus.length === 0) {
    //   return message.warning(t('knowledge.无未配置的SKU'));
    // }
    // if (!knowledgeValueId) {
    //   return message.warning(t('请选择知识值'));
    // }

    if (!selectedKnowledge) return;
    setSaveLoading(true);
    update({
      spuKnowledge: {
        ...selectedKnowledge,
        values: map(selectedKnowledge.values, (each) => {
          const { spus, originSpus, ...rest } = each;
          return {
            ...rest,
            spus: originSpus,
          };
        }),
      } as any,
    })
      .then(() => {
        return refreshKnowledgeDetail().then(() => {
          message.success(t('已保存'));
          if (next) {
            if (selectedKnowledgeIndex < knowledges.length - 1) {
              setSelectedKnowledgeId(sortedKnowledges[selectedKnowledgeIndex + 1].id);
              setKnowledgeValueId(undefined!);
            }
          }
        });
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };
  const handleMove = () => {
    if (!knowledgeValueId) {
      return message.warning(t('请选择知识值'));
    }
    setMidKnowledges((prevState) => {
      const ks = cloneDeep(prevState || []);
      const index = findIndex(ks, (k) => k.id === selectedKnowledgeId);
      const newk = ks[index];
      const partSpus = getSpuInfoBySelectMap(items, spuSelectMap).map((e) =>
        transformItemV2Spu2KnowledgeSpu(e),
      );
      newk.values = newk.values.map((v) => {
        if (v.id === knowledgeValueId) {
          return {
            ...v,
            spus: mergeItems(v.spus!, partSpus),
          };
        } else {
          return {
            ...v,
            spus: splitItems(v.spus!, partSpus),
          };
        }
      }) as any;
      ks.splice(index, 1, newk);
      return ks;
    });
  };

  // 这里是编辑已配置的sku的逻辑 end
  useEffect(() => {
    setKnowledgeValueId(undefined!);
    // 当知识点变更，知识值的id需要重置
  }, [selectedKnowledgeId]);

  const renderKnowledges = useMemo(() => {
    return filter(sortedKnowledges, (each) => !shutDownInfo.includes(each.id));
  }, [shutDownInfo, sortedKnowledges]);

  let step2Node = null;
  if (loading) {
    step2Node = (
      <div className="h-full flex items-center justify-center">
        <Spin />
      </div>
    );
  } else {
    if (get(renderKnowledges, 'length', 0) === 0) {
      step2Node = (
        <div className="h-full flex items-center justify-center">
          <Empty description={t('storeSelector.暂无数据')} />
        </div>
      );
    } else {
      step2Node = (
        <>
          <div className="h-[48px] px-[16px] flex items-center text-[14px] font-medium border-0 border-b border-solid border-[#f0f2f5] flex-shrink-0">
            <Badge
              className={css`
                .ant-badge-status-dot {
                  width: 8px;
                  height: 8px;
                  background-color: #00bf69;
                  &::after {
                    border-color: #00bf69 !important;
                  }
                }
                .ant-badge-status-text {
                  font-size: 16px;
                }
              `}
              status="processing"
              text={`${spuClassIds ? '' : 'STEP2 '}${t('点击要配置的知识点或点击下一步进行配置')}`}
            />
          </div>
          <div className="flex-grow flex flex-col overflow-hidden px-[16px]">
            <div className="h-[42px] flex items-center justify-between flex-shrink-0">
              <div className="text-[#84868c]">
                {t('ViewDashboard.共x项', { x: get(knowledges, 'length', 0) })}
              </div>
              <div>
                <Dropdown
                  className="ml-[8px] cursor-pointer"
                  trigger={['click']}
                  visible={knowledgeSortVisible}
                  onVisibleChange={setKnowledgeSortVisible}
                  overlay={
                    <Menu
                      onClick={(e) => {
                        setKnowledgeSort(e.key as any);
                        setKnowledgeSortVisible(false);
                      }}
                      selectedKeys={[knowledgeSort]}
                    >
                      <Menu.Item key="1">{t('智能推荐')}</Menu.Item>
                      <Menu.Item key="2">{t('配置完成度由低到高')}</Menu.Item>
                    </Menu>
                  }
                >
                  <div className="max-w-[240px] overflow-hidden flex items-center">
                    <SwapOutlined
                      className={css`
                        transform: rotate(90deg);
                        margin-right: 4px;
                      `}
                    />
                    <Space>
                      {knowledgeSort === '1' ? t('智能推荐') : t('配置完成度由低到高')}
                      <DownOutlined />
                    </Space>
                  </div>
                </Dropdown>
              </div>
            </div>
            <div className="flex-grow overflow-y-auto flex flex-wrap gap-x-[16px] gap-y-[12px] content-start">
              {renderKnowledges?.map((each) => {
                return (
                  <div
                    key={each.id}
                    onClick={() => {
                      setSelectedKnowledgeId(each.id);
                    }}
                    className="basis-[calc(50%-8px)] flex-grow-0 bg-[#f7f9fa] h-[48px] flex items-center justify-between px-[16px] overflow-hidden cursor-pointer"
                  >
                    <div className="mr-[6px] text-[#515359] flex overflow-hidden items-center">
                      <span className="truncate">
                        {getMultiLangValue(each.nameMultiLang, lang, each.knowledgeName)}
                      </span>
                      {each.valueAsDirectAnswer && (
                        <Tooltip title={t('建议配置具体话术，机器人将使用所配置的话术回答')}>
                          <Icon component={DangerSvg} />
                        </Tooltip>
                      )}
                    </div>
                    <div
                      className="flex items-center"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <SwitchKnowledgeStatus id={each.id} knowledges={_knowledges} />
                      <div className="w-[1px] h-[16px] bg-[#e1e4eb] mx-[13px]"></div>
                      <ProgressBar
                        progress={get(knowledgeCompleteness, each.id, 0)}
                        className="flex-shrink-0"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      );
    }
  }

  const itemView = (
    <>
      {selectedKnowledge && (
        <div className="h-[44px] flex-shrink-0 flex justify-between item-center px-[15px]">
          <div className="flex items-center">
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              onChange={(e) => {
                toggleBatch(filterSpusIds, e.target.checked);
              }}
            >
              {t('marketing.全选')}
            </Checkbox>
            <div className="mr-[16px] text-[#84868c]">
              {t('voucher.已选x', { x: selectedSkuLen })} SKU
            </div>
          </div>
        </div>
      )}
      <div className="flex-grow px-[16px] pb-[16px] overflow-y-auto">
        {filterSpus.length ? (
          filterSpus.map((each) => {
            return (
              <ItemRender
                className={css`
                  box-sizing: border-box;
                  border: 1px solid rgba(211, 215, 224, 1);
                  border-radius: 2px;
                  padding: 10px 13px 12px 8px;
                  margin-bottom: 16px;
                  &:last-child {
                    margin-bottom: 0;
                  }
                `}
                item={each}
                selectedInfo={spuSelectMap[each.internalItemId] || {}}
                configedInfo={configedSpusMap}
                showConfigTag={!!selectedKnowledge}
                toggleSku={toggleSku}
                toggleSpu={toggleSpu}
                key={each.internalItemId}
              />
            );
          })
        ) : (
          <div className="h-full flex items-center justify-center">
            <Empty description={t('knowledge.无未配置的SKU')} />
          </div>
        )}
      </div>
    </>
  );

  let step3Node = null;
  const activeValueIndex = renderKnowledgeValues.findIndex((each) => each?.id === knowledgeValueId);
  const boxRef = useRef<HTMLDivElement>(null);
  const boxHeight = useSize(boxRef)?.height || 0;
  const halfHeight = (boxHeight + 140 + 56) / 2;
  const top = useScroll(boxRef)?.top || 0;
  const [offsetTop, setOffsetTop] = useState(0);
  const buttonCenterAnchor = halfHeight - 140 + 24;
  const mayBeTop = offsetTop + 27;
  const mayBeBottom = boxHeight - offsetTop - 27;
  const linecss =
    mayBeTop - top < buttonCenterAnchor
      ? css`
          top: ${mayBeTop}px;
          height: ${buttonCenterAnchor - mayBeTop + top}px;
        `
      : css`
          height: ${offsetTop - buttonCenterAnchor + 24 - top}px;
          bottom: ${mayBeBottom}px;
        `;
  useLayoutEffect(() => {
    const activeTarget = document.querySelector('.box .active');
    if (activeTarget) {
      const offsetTop = activeTarget.offsetTop;
      setOffsetTop(offsetTop);
    }
  }, [activeValueIndex, top]);
  const [toolTipVisible, setToolTipVisible] = useState(true);
  const checkDirty = (cb?: Function) => {
    // 检查当前知识点是否有SPU未保存
    const spus = get(
      find(_knowledges, (v) => v.id === selectedKnowledgeId),
      'values',
      [],
    );
    const initialSpus = get(
      find(midKnowledges, (v) => v.id === selectedKnowledgeId),
      'values',
      [],
    );

    if (isEqual(spus, initialSpus)) {
      cb?.();
    } else {
      Modal.confirm({
        title: t('确认要离开吗'),
        content: t('存在SPU未保存知识值配置'),
        onOk: () => {
          cb?.();
        },
      });
    }
  };
  if (selectedKnowledgeId) {
    if (selectedKnowledge) {
      step3Node = (
        <>
          <div className="flex-grow flex overflow-hidden">
            <div className="flex-shrink-0 w-[204px] bg-[#fff] overflow-y-auto px-[12px] pt-[18px] border border-solid border-[#f0f2f5]">
              {renderKnowledges.map((each) => {
                const name = getMultiLangValue(each.nameMultiLang, lang, each.knowledgeName);
                return (
                  <div
                    key={each.id}
                    onClick={() => {
                      checkDirty(() => {
                        setSelectedKnowledgeId(each.id);
                      });
                    }}
                    className={cx(
                      'rounded-[3px] relative basis-[calc(50%-8px)] flex-grow-0 bg-[#f7f9fa] h-[48px] flex items-center justify-between px-[16px] overflow-hidden cursor-pointer mb-[12px]',
                      css`
                        &::after {
                          content: '';
                          display: none;
                          position: absolute;
                          top: 0;
                          right: 0;
                          bottom: 0;
                          width: 3px;
                          background: #3366ff;
                        }

                        &.active::after {
                          display: block;
                        }
                      `,
                      {
                        active: each.id === selectedKnowledgeId,
                      },
                    )}
                  >
                    <div className="mr-[6px] text-[#515359] flex overflow-hidden items-center">
                      <span className="truncate" title={name}>
                        {name}
                      </span>
                      {each.valueAsDirectAnswer && (
                        <Tooltip title={t('建议配置具体话术，机器人将使用所配置的话术回答')}>
                          <Icon component={DangerSvg} />
                        </Tooltip>
                      )}
                    </div>
                    <ProgressBar
                      progress={get(knowledgeCompleteness, each.id, 0)}
                      className="flex-shrink-0"
                    />
                  </div>
                );
              })}
            </div>
            {!!filterSpus.length && (
              <Tooltip
                title={t('移动至右侧知识值中')}
                visible={toolTipVisible && !visible1 && !visible2}
              >
                <Button
                  disabled={saveLoading || selectedSkuLen === 0}
                  onMouseLeave={() => {
                    setToolTipVisible(false);
                  }}
                  onClick={() => {
                    handleMove();
                  }}
                  size="small"
                  className="!absolute top-[50%] left-[680px] !w-[24px] !h-[48px] z-10 [&_button]:!h-[48px]"
                  type="primary"
                  icon={<RightOutlined style={{ fontSize: 9 }} />}
                ></Button>
              </Tooltip>
            )}
            <div className="flex-grow flex flex-col overflow-hidden bg-[#fff] border border-l-0 border-solid border-[#f0f2f5]">
              <div className="h-[48px] px-[16px] flex items-center text-[14px] font-medium border-0 border-b border-solid border-[#f0f2f5] flex-shrink-0">
                <div className="flex items-center justify-between flex-grow">
                  <Badge
                    className={css`
                      .ant-badge-status-dot {
                        width: 8px;
                        height: 8px;
                        background-color: #00bf69;

                        &::after {
                          border-color: #00bf69 !important;
                        }
                      }

                      .ant-badge-status-text {
                        font-size: 16px;
                      }
                    `}
                    status="processing"
                    text={`${spuClassIds ? '' : 'STEP3 '}${t(
                      '选择左侧未配置的商品移动到右侧知识值中',
                    )}`}
                  />
                  {visible1 && meta1 && (
                    <EditKCDrawer visible={visible1} {...meta1} onClose={() => hide1()} />
                  )}
                  {visible2 && meta2 && (
                    <EditDangerousKCDrawer visible={visible2} {...meta2} onClose={() => hide2()} />
                  )}
                  <CollapseOperationInSingleClassNew className="text-[#515359] font-normal text-[14px]" />
                </div>
              </div>
              <div className="flex-grow flex overflow-hidden">
                <div className="w-[488px] flex-shrink-0 border-0 border-r border-solid border-[#f0f2f5] overflow-hidden flex flex-col">
                  {itemView}
                </div>
                <div className="flex-grow overflow-hidden flex flex-col">
                  <div className="h-[44px] flex items-center justify-end pr-[16px] flex-shrink-0">
                    <Button onClick={() => handleKc()} type="link" className="!pr-0">
                      + {t('knowledge.新增x', { x: t('knowledge.知识值') })}
                    </Button>
                  </div>
                  {selectedKnowledge.valueAsDirectAnswer && (
                    <div className="flex-shrink-0 flex items-center px-[14px] py-[7px] leading-[18px] text-[12px] text-[#F05B5B] bg-[#FFF0F0]">
                      <Icon className="mr-[6px]" component={DangerSvg} />
                      {t('建议配置具体话术，机器人将使用所配置的话术回答')}
                    </div>
                  )}
                  {renderKnowledgeValues.length ? (
                    <div
                      className="relative mt-[16px] px-[16px] pb-[16px] flex-grow overflow-y-auto box"
                      ref={boxRef}
                    >
                      {/*箭头和线*/}
                      {!!filterSpus.length && activeValueIndex > -1 && (
                        <div
                          className={cx(
                            'w-[11px] h-[1px] bg-[#3360ff] absolute left-[6px]',
                            css`
                              top: ${offsetTop + 27}px;

                              &::before {
                                content: '';
                                display: block;
                                position: absolute;
                                top: 0;
                                right: 0;
                                width: 9px;
                                height: 1px;
                                background: #3360ff;
                                transform: rotate(45deg);
                                transform-origin: right bottom;
                              }

                              &::after {
                                content: '';
                                display: block;
                                position: absolute;
                                top: 0;
                                right: 0;
                                width: 9px;
                                height: 1px;
                                background: #3360ff;
                                transform: rotate(-45deg);
                                transform-origin: right bottom;
                              }
                            `,
                          )}
                        ></div>
                      )}
                      {!!filterSpus.length && activeValueIndex > -1 && (
                        <div
                          className={cx('absolute w-[1px] bg-[#3360ff] left-[6px]', linecss)}
                        ></div>
                      )}

                      {renderKnowledgeValues.map((each) => {
                        const segments = getSegmentsValue(each || {});
                        const text = segments2text(t, segments);
                        const hasDefaultValue = find(
                          selectedKnowledge.values,
                          (e) => e.isDefaultValue && e.id !== each.id,
                        );
                        return (
                          <div
                            onClick={() => setKnowledgeValueId(each.id)}
                            key={each.id}
                            className={cx(
                              'border border-solid border-[#d3e7e0] rounded-[4px] mb-[16px] last:mb-0 overflow-hidden',
                              {
                                '!border-[#336fff]': each.id === knowledgeValueId,
                                active: each.id === knowledgeValueId,
                              },
                            )}
                          >
                            <div className="flex py-[15px] pl-[12px] pr-[16px] items-center relative">
                              {each.isDefaultValue && (
                                <div className="absolute left-0 top-0 bg-[#EBFAF3] leading-[14px] text-[#00BF69] text-[12px] px-[4px]">
                                  {t('默认值')}
                                </div>
                              )}
                              <Radio
                                className="flex-shrink-0 mr-[8px]"
                                checked={knowledgeValueId === each.id}
                              />
                              <div className="flex-grow overflow-hidden flex items-center">
                                <div className="flex-grow line-clamp-2 break-all text-[#515359]">
                                  {text}
                                </div>
                                <ConfigDefaultIcon
                                  isDefaultValue={each.isDefaultValue}
                                  hasDefaultValue={hasDefaultValue}
                                  knowledgeId={selectedKnowledgeId}
                                  knowledgeValueId={each.id}
                                  onSuccess={() => {
                                    refreshKnowledgeDetail();
                                  }}
                                />
                                <EditOutlined
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleKc(each);
                                  }}
                                  className="flex-shrink-0 !mt-[2px] ml-[16px] !text-[#515359]"
                                />
                              </div>
                            </div>
                            <ConfigedSkuButton
                              active={each.id === knowledgeValueId}
                              selectedSpus={each.spus}
                              selectedKnowledgeId={selectedKnowledgeId}
                              selectedKnowledgeValueId={knowledgeValueId}
                              setKnowledges={setMidKnowledges}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Empty description={t('无知识值')}>
                        <Button onClick={() => handleKc()} type="link">
                          + {t('knowledge.新增x', { x: t('knowledge.知识值') })}
                        </Button>
                      </Empty>
                    </div>
                  )}
                </div>
              </div>
              <div
                className={cx(
                  'bg-[#fff] h-[56px] px-[16px] items-center justify-between flex-shrink-0 flex border border-t-0 border-solid border-[#f0f2f5]',
                  css`
                    box-shadow: 0 -2px 8px 0 #a7b2cc1a, inset 0 1px 0 0 #f0f2f5;
                  `,
                )}
              >
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    checkDirty(() => setSelectedKnowledgeId(undefined!));
                  }}
                >
                  <LeftOutlined />
                  {t('phrase.返回')}
                </div>
                <div className="flex gap-[16px]">
                  <Button
                    loading={saveLoading}
                    type="primary"
                    onClick={() => {
                      handleSave();
                    }}
                  >
                    {t('phrase.保存')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }
  }
  const shutDownKnowledges = useMemo(() => {
    return filter(_knowledges, (each) => each.isShutdown);
  }, [_knowledges]);

  const content = (
    <div className="h-full overflow-hidden flex relative">
      {step3Node || (
        <>
          <div className="w-[462px] flex-shrink-0 bg-[#fff] border border-solid border-[#f0f2f5] overflow-hidden flex flex-col">
            <div className="h-[48px] px-[16px] flex justify-between items-center border-0 border-b border-solid border-[#f0f2f5] flex-shrink-0">
              <span className="text-[12px] text-[#84868c]">
                {spuClassIds ? t('焦点商品') : t('voucher.已选商品')}
              </span>
              <CollapseOperationInSingleClassNew className="text-[#515359]" />
            </div>
            {(spuClassIds?.length || 0) > 1 && (
              <Tabs
                activeKey={String(activeIndex)}
                onChange={(index) => {
                  setActiveIndex(Number(index));
                }}
                className={css`
                  .ant-tabs-tab {
                    padding: 0;
                  }
                  .ant-tabs-nav {
                    padding: 16px 16px 0;
                    margin-bottom: 0;
                    &::before {
                      border: none;
                    }
                  }
                  .ant-tabs-ink-bar {
                    display: none;
                  }
                  .ant-tabs-tab {
                    & + .ant-tabs-tab {
                      margin-left: 16px !important;
                    }
                    &.ant-tabs-tab-active {
                      & > div {
                        background: #f0f7ff;
                      }
                    }
                    & > div {
                      padding: 5px 16px;
                      border-radius: 16px;
                      background: #f7f9fa;
                      color: #515359;
                    }
                  }
                `}
              >
                {list.map(({ id, name, nameMultiLang }, index) => {
                  if (!spuClassIds?.includes(id)) {
                    return null;
                  }
                  const n = getMultiLangValue(nameMultiLang, lang, name);
                  return <Tabs.TabPane key={String(index)} tab={n} />;
                })}
              </Tabs>
            )}
            <div className="flex-grow overflow-hidden flex flex-col pt-[16px]">{itemView}</div>
          </div>
          <div className="flex-grow bg-[#fff] border border-l-0 border-solid border-[#f0f2f5] overflow-hidden flex flex-col">
            {step2Node}
            <div
              className={cx(
                'flex-shrink-0 h-[56px] flex items-center px-[16px] justify-between',
                css`
                  box-shadow: 0 -2px 8px 0 #a7b2cc1a, inset 0 1px 0 0 #f0f2f5;
                `,
              )}
            >
              <ClosedKnowledge
                knowledges={shutDownKnowledges}
                singleClass={activeSpuClass!}
                successCallback={(ids) => {
                  const newArr = cloneDeep(_knowledges);
                  forEach(newArr, (e) => {
                    if (ids.includes(e.id)) {
                      e.isShutdown = false;
                    }
                  });
                  setFieldValue('knowledges', newArr);
                }}
                placeholder={<div />}
              />
              <Button
                type="primary"
                disabled={renderKnowledges.length === 0}
                onClick={() => {
                  setSelectedKnowledgeId(renderKnowledges[0].id);
                }}
              >
                {t('authShop.下一步')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (spuClassIds) {
    return content;
  }

  return (
    <Drawer
      className={cx(
        css`
          .ant-drawer-body {
            padding: 16px;
            background: #f8f9fa;
          }
        `,
        className,
      )}
      width={1280}
      title={t('配置知识值')}
      {...restProps}
    >
      {content}
    </Drawer>
  );
});

ConfigKC.displayName = 'SingleClassNewConfigKC';

export default ConfigKC;

interface ItemRenderProps extends HTMLAttributes<HTMLDivElement> {
  item: ItemSelectV2.Item;
  selectedInfo: SpuSelectMap[string];
  toggleSku: ToggleSku;
  toggleSpu: ToggleSpu;
  configedInfo?: Record<string, any>;
  showConfigTag?: boolean;
}

const ItemRender = memo((props: ItemRenderProps) => {
  const {
    className,
    configedInfo = {},
    showConfigTag = false,
    toggleSku,
    toggleSpu,
    selectedInfo,
    item,
    ...rest
  } = props;
  const { t } = useTranslate();
  const {
    itemName,
    imageUrl: [imageUrl] = [],
    externalItemId: spuId,
    internalItemId,
    skuInfo = [],
    itemUrl,
    itemSku,
  } = item;
  const foldState = useAtomValue(collapseOperationState);
  const storeStr = useStoreInfo(item);
  const [open, setOpen] = useState<'fold' | 'unfold'>('fold');
  useEffect(() => {
    setOpen(foldState);
  }, [foldState]);
  const unConfigSkuLength = useMemo(() => {
    const target = configedInfo[internalItemId];
    const configedLength = filter(skuInfo, (each) => {
      return target?.[each.skuId];
    }).length;
    return skuInfo.length - configedLength;
  }, [internalItemId, skuInfo, configedInfo]);

  return (
    <div className={className} {...rest}>
      <div className="flex items-center">
        {showConfigTag && (
          <Checkbox
            className="!mr-[8px] flex-shrink-0"
            checked={selectedInfo.checked}
            indeterminate={selectedInfo.indeterminate}
            onChange={(e) => {
              toggleSpu(internalItemId, e.target.checked);
            }}
          />
        )}
        <div className="basis-auto overflow-hidden flex flex-grow">
          <div className="flex-shrink-0 !mr-[8px]">
            <Image hasSmallPic="both" src={imageUrl} width={68} height={68} />
          </div>
          <div className="basis-auto flex-grow overflow-hidden flex flex-col">
            <div className="h-[20px] flex items-start flex-shrink-0">
              <div
                title={itemName}
                className="w-full group line-clamp-1 break-all cursor-pointer leading-[20px] hover:text-[#3366ff]"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(itemUrl, '_blank');
                }}
              >
                <LinkOutlined className="!hidden float-right bg-[#d6e8ff] !text-[#336fff] h-[20px] w-[20px] !leading-[20px] group-hover:!block" />
                {itemName}
              </div>
            </div>
            <div className="mt-[4px] leading-[18px] text-[12px] text-[#84868c] flex-grow flex flex-col justify-between">
              <div className="truncate" title={storeStr}>
                {t('marketing.店铺')}: {storeStr}
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center truncate basis-auto overflow-hidden mr-[8px]">
                  <span>ID: {spuId}</span>
                  <div className="mx-[4px]">|</div>
                  <span className="truncate">
                    {t('aide.主商品货号')}: {itemSku || '-'}
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center justify-end">
                <div>
                  {unConfigSkuLength > 0 &&
                    unConfigSkuLength !== skuInfo.length &&
                    `(${t('X 个未配置', { x: unConfigSkuLength })})`}
                </div>
                <div
                  onClick={() => {
                    setOpen(open === 'fold' ? 'unfold' : 'fold');
                  }}
                  className="cursor-pointer hover:text-[#3366ff]"
                >
                  {t('knowledge.共{{x}}个SKU', { x: skuInfo?.length })}
                  {open === 'fold' ? (
                    <DownOutlined className="ml-[4px]" />
                  ) : (
                    <UpOutlined className="ml-[4px]" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {open === 'unfold' && skuInfo.length > 0 && (
        <div
          className={cx('mt-[8px]', {
            'pl-[22px]': showConfigTag,
          })}
        >
          <div className="border-0 border-t border-solid border-[#f0f2f5] overflow-hidden">
            {skuInfo.map((sku) => {
              const node = (
                <div
                  title={`${sku.sellerSku}(${sku.skuName})`}
                  className="flex items-center overflow-hidden gap-[6px]"
                >
                  <div className="w-full truncate break-all">
                    {sku.sellerSku}({sku.skuName})
                  </div>
                </div>
              );
              return (
                <div key={sku.skuId} className="flex-shrink-0 flex-grow-0 mt-[9px] overflow-hidden">
                  {showConfigTag ? (
                    <Checkbox
                      className={css`
                        overflow: hidden;
                        width: 100%;
                        word-break: break-all;

                        span + span {
                          width: 100%;
                          padding-right: 0 !important;
                          overflow: hidden;
                        }
                      `}
                      checked={selectedInfo?.skus?.[sku.skuId]?.checked}
                      onChange={(e) => {
                        toggleSku(internalItemId, sku.skuId, e.target.checked);
                      }}
                    >
                      {node}
                    </Checkbox>
                  ) : (
                    node
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

ItemRender.displayName = 'ConfigKCItemRender';

const SwitchKnowledgeStatus = (props: { knowledges: Knowledge[]; id: string }) => {
  const { knowledges, id } = props;
  const { t } = useTranslate();
  const { setFieldValue } = useFormikContext<SpuAreaFormikValue>();
  const { runAsync: closeKnowledge, loading } = useRequest(
    (id: string) =>
      updateKnowledgeStatus({
        openIds: [],
        shutdownIds: [id],
      }),
    {
      onSuccess: (_, params) => {
        const id = params[0];
        const newArr = cloneDeep(knowledges);
        const index = findIndex(newArr, { id });
        newArr[index].isShutdown = true;
        setFieldValue('knowledges', newArr);
      },
      onError: () => {
        message.error(t('translation.操作失败，请稍后再试'));
      },
      manual: true,
    },
  );
  return (
    <Popconfirm
      title={
        <div className="max-w-[280px]">
          <div>{t('你确定不启用吗')}</div>
          <div>{t('不启用后，机器人将不再使用该知识点下的内容进行任何商品的回复')}</div>
        </div>
      }
      onConfirm={(e) => {
        e?.stopPropagation();
        closeKnowledge(id);
      }}
      okText={t('phrase.确定')}
      cancelText={t('phrase.取消')}
    >
      <Tooltip title={t('不启用该知识点')}>
        <Switch size="small" checked loading={loading} />
      </Tooltip>
    </Popconfirm>
  );
};

const Image = styled(SafeImage)`
  width: 68px;
  height: 68px;
`;
